import {
  SEGMENT_FIELD_TYPES,
  SEGMENT_TYPES,
  URL_MAX_LENGTH,
  IMAGE_POSITION_OPTIONS,
  MEGA_MENU_STYLE_OPTIONS,
  MEGA_MENU_CONTENT_OPTIONS,
  MEGA_MENU_DISPLAY_OPTIONS,
  MEGA_MENU_HIERARCHY_OPTIONS,
  STYLE_OPTIONS,
  HERO_CTA_ICON_OPTIONS,
  field,
  visualStyleProperty,
  globalColorProperties,
  commonSegment
} from "./segmentRegistryCore";

const HERO_CTA_FONT_WEIGHT_OPTIONS = Object.freeze([
  { value: "", label: "Actual / heredado" },
  { value: 300, label: "Liviano (300)" },
  { value: 400, label: "Normal (400)" },
  { value: 500, label: "Medio (500)" },
  { value: 600, label: "Semibold (600)" },
  { value: 700, label: "Negrita (700)" },
  { value: 800, label: "Extra negrita (800)" },
  { value: 900, label: "Black (900)" },
]);

function heroCtaStyleProperties(stylePath, label) {
  return [
    field(`${stylePath}.textColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: `${label} · texto`,
      uiGroup: "design",
    }),
    field(`${stylePath}.backgroundColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: `${label} · fondo`,
      uiGroup: "design",
    }),
    field(`${stylePath}.backgroundOpacity`, SEGMENT_FIELD_TYPES.NUMBER, {
      label: `${label} · opacidad del fondo`,
      limits: { min: 0, max: 1, step: 0.05 },
      uiGroup: "design",
    }),
    field(`${stylePath}.hoverTextColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: `${label} · texto al pasar el mouse`,
      uiGroup: "design",
    }),
    field(`${stylePath}.hoverBackgroundColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: `${label} · fondo al pasar el mouse`,
      uiGroup: "design",
    }),
    field(`${stylePath}.hoverBackgroundOpacity`, SEGMENT_FIELD_TYPES.NUMBER, {
      label: `${label} · opacidad hover`,
      limits: { min: 0, max: 1, step: 0.05 },
      uiGroup: "design",
    }),
    field(`${stylePath}.borderColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: `${label} · borde`,
      uiGroup: "design",
    }),
    field(`${stylePath}.borderWidth`, SEGMENT_FIELD_TYPES.NUMBER, {
      label: `${label} · ancho de borde (px)`,
      limits: { min: 0, max: 16, step: 0.5 },
      uiGroup: "design",
    }),
    field(`${stylePath}.borderRadius`, SEGMENT_FIELD_TYPES.NUMBER, {
      label: `${label} · radio de borde (px)`,
      limits: { min: 0, max: 999, step: 1 },
      uiGroup: "design",
    }),
    field(`${stylePath}.fontSize`, SEGMENT_FIELD_TYPES.NUMBER, {
      label: `${label} · tamaño de texto (px)`,
      limits: { min: 8, max: 72, step: 1 },
      uiGroup: "design",
    }),
    field(`${stylePath}.fontWeight`, SEGMENT_FIELD_TYPES.SELECT, {
      label: `${label} · peso tipográfico`,
      options: HERO_CTA_FONT_WEIGHT_OPTIONS,
      uiGroup: "design",
    }),
    field(`${stylePath}.minWidth`, SEGMENT_FIELD_TYPES.NUMBER, {
      label: `${label} · ancho mínimo (px)`,
      limits: { min: 0, max: 600, step: 1 },
      uiGroup: "design",
    }),
    field(`${stylePath}.minHeight`, SEGMENT_FIELD_TYPES.NUMBER, {
      label: `${label} · alto mínimo (px)`,
      limits: { min: 0, max: 160, step: 1 },
      uiGroup: "design",
    }),
    field(`${stylePath}.paddingX`, SEGMENT_FIELD_TYPES.NUMBER, {
      label: `${label} · padding horizontal (px)`,
      limits: { min: 0, max: 64, step: 1 },
      uiGroup: "design",
    }),
    field(`${stylePath}.paddingY`, SEGMENT_FIELD_TYPES.NUMBER, {
      label: `${label} · padding vertical (px)`,
      limits: { min: 0, max: 48, step: 1 },
      uiGroup: "design",
    }),
    field(`${stylePath}.shadowColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: `${label} · color de sombra`,
      uiGroup: "design",
    }),
    field(`${stylePath}.shadowOpacity`, SEGMENT_FIELD_TYPES.NUMBER, {
      label: `${label} · opacidad de sombra`,
      limits: { min: 0, max: 1, step: 0.05 },
      uiGroup: "design",
    }),
    field(`${stylePath}.shadowX`, SEGMENT_FIELD_TYPES.NUMBER, {
      label: `${label} · sombra X (px)`,
      limits: { min: -100, max: 100, step: 1 },
      uiGroup: "design",
    }),
    field(`${stylePath}.shadowY`, SEGMENT_FIELD_TYPES.NUMBER, {
      label: `${label} · sombra Y (px)`,
      limits: { min: -100, max: 100, step: 1 },
      uiGroup: "design",
    }),
    field(`${stylePath}.shadowBlur`, SEGMENT_FIELD_TYPES.NUMBER, {
      label: `${label} · blur de sombra (px)`,
      limits: { min: 0, max: 160, step: 1 },
      uiGroup: "design",
    }),
    field(`${stylePath}.shadowSpread`, SEGMENT_FIELD_TYPES.NUMBER, {
      label: `${label} · spread de sombra (px)`,
      limits: { min: -80, max: 80, step: 1 },
      uiGroup: "design",
    }),
  ];
}

const NAVBAR_ELEMENT_TARGETS = Object.freeze({
  "@navbar.logo": Object.freeze({
    kind: "image",
    label: "Logo",
    fieldPath: "theme.logoUrl",
    mobileVariantPath: "theme.compactLogoUrl",
    showPath: "navigation.showLogo",
    chips: ["Desktop", "Mobile"],
  }),
  "business.name": Object.freeze({
    kind: "text",
    label: "Nombre del negocio",
  }),
  "navigation.homeLabel": Object.freeze({
    kind: "button",
    label: "Inicio",
    labelPath: "navigation.homeLabel",
    urlPath: "navigation.homeUrl",
    chips: ["Desktop", "Mobile", "Link"],
  }),
  "navigation.aboutLabel": Object.freeze({
    kind: "button",
    label: "Sobre nosotros",
    labelPath: "navigation.aboutLabel",
    urlPath: "navigation.aboutUrl",
    showPath: "navigation.showAboutLink",
    chips: ["Desktop", "Mobile", "Link"],
  }),
  "navigation.highlightsLabel": Object.freeze({
    kind: "button",
    label: "Diferenciales",
    labelPath: "navigation.highlightsLabel",
    urlPath: "navigation.highlightsUrl",
    showPath: "navigation.showHighlightsLink",
    chips: ["Desktop", "Mobile", "Link"],
  }),
  "navigation.productsLabel": Object.freeze({
    kind: "button",
    label: "Productos o servicios",
    labelPath: "navigation.productsLabel",
    urlPath: "navigation.productsUrl",
    showPath: "navigation.showProductsLink",
    megaMenu: true,
    chips: ["Desktop", "Mobile", "Mega menú"],
  }),
  "navigation.contactLabel": Object.freeze({
    kind: "button",
    label: "Contacto",
    labelPath: "navigation.contactLabel",
    urlPath: "navigation.contactUrl",
    showPath: "navigation.showContactLink",
    chips: ["Desktop", "Mobile", "Link"],
  }),
  "navigation.loginLabel": Object.freeze({
    kind: "button",
    label: "Acceso clientes",
    labelPath: "navigation.loginLabel",
    urlPath: "navigation.loginUrl",
    showPath: "navigation.showCustomerAccess",
    extraPaths: ["navigation.accountLabel"],
    chips: ["Desktop", "Mobile", "Link"],
  }),
  "@navbar.mobile-menu": Object.freeze({
    kind: "navbar-mobile",
    label: "Menú móvil",
    fieldPrefix: "navigation.mobile.",
    extraPaths: [
      "navigation.showSocialLinks",
      "navigation.menuAriaLabel",
      "navigation.closeMenuAriaLabel",
    ],
    chips: ["Mobile"],
  }),
  "@navbar.buttons-manager": Object.freeze({
    kind: "collection-manager",
    label: "Administrar botones",
    collectionPath: "navigation.buttons",
    chips: ["Desktop", "Mobile"],
  }),
});

const NAVBAR_SHELL_PROPERTY_PATHS = Object.freeze([
  "navigation.style",
  "navigation.showCart",
  "navigation.cartAriaLabel",
]);


const HERO_ELEMENT_TARGETS = Object.freeze({
  "@hero.slides-manager": Object.freeze({
    kind: "collection-manager",
    label: "Slider de portada",
    collectionPath: "hero.slides",
    editorMode: "slides",
    chips: ["Slider"],
  }),
  "@hero.rotating-phrases": Object.freeze({
    kind: "collection-manager",
    label: "Frases rotativas",
    collectionPath: "hero.rotatingPhrases",
    editorMode: "phrases",
    chips: ["Texto dinámico"],
  }),
});

const HERO_SHELL_PROPERTY_PATHS = Object.freeze([
  "hero.contentHorizontalAlign",
  "hero.contentVerticalAlign",
  "hero.overlay",
]);

const structureSegments = {
  [SEGMENT_TYPES.NAVBAR]: commonSegment({
    id: SEGMENT_TYPES.NAVBAR,
    visibilityPath: "sections.showNavbar",
    elementEditingReady: true,
    elementTargets: NAVBAR_ELEMENT_TARGETS,
    shellPropertyPaths: NAVBAR_SHELL_PROPERTY_PATHS,
    properties: [
      field("sections.showNavbar", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar encabezado",
      }),
      field("navigation.style", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Estilo del encabezado",
        options: STYLE_OPTIONS,
      }),
      field("navigation.showLogo", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar logo en la barra",
      }),
      field("navigation.mobile.fontSize", SEGMENT_FIELD_TYPES.NUMBER, {
        label: "Vista móvil · tamaño de texto (px)",
        limits: { min: 8, max: 40, step: 1 },
        uiGroup: "design",
      }),
      field("navigation.mobile.fontWeight", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Vista móvil · peso tipográfico",
        options: HERO_CTA_FONT_WEIGHT_OPTIONS,
        uiGroup: "design",
      }),
      field("navigation.mobile.itemMinHeight", SEGMENT_FIELD_TYPES.NUMBER, {
        label: "Vista móvil · alto mínimo de cada opción (px)",
        limits: { min: 32, max: 96, step: 1 },
        uiGroup: "design",
      }),
      field("navigation.mobile.itemGap", SEGMENT_FIELD_TYPES.NUMBER, {
        label: "Vista móvil · separación entre opciones (px)",
        limits: { min: 0, max: 32, step: 1 },
        uiGroup: "design",
      }),
      field("navigation.mobile.drawerBackgroundColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Vista móvil · fondo del drawer",
        uiGroup: "design",
      }),
      field("navigation.mobile.drawerTextColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Vista móvil · texto principal",
        uiGroup: "design",
      }),
      field("navigation.mobile.drawerMutedTextColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Vista móvil · texto secundario",
        uiGroup: "design",
      }),
      field("navigation.mobile.drawerDividerColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Vista móvil · divisores y bordes",
        uiGroup: "design",
      }),
      field("navigation.mobile.itemHoverBackgroundColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Vista móvil · fondo hover/seleccionado",
        uiGroup: "design",
      }),
      field("navigation.mobile.hamburgerColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Vista móvil · color hamburguesa",
        uiGroup: "design",
      }),
      field("navigation.mobile.hamburgerBackgroundColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Vista móvil · fondo hamburguesa",
        uiGroup: "design",
      }),
      field("navigation.mobile.hamburgerHoverColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Vista móvil · color hamburguesa hover",
        uiGroup: "design",
      }),
      field("navigation.mobile.hamburgerHoverBackgroundColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Vista móvil · fondo hamburguesa hover",
        uiGroup: "design",
      }),
      field("navigation.mobile.hamburgerBorderColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Vista móvil · borde hamburguesa",
        uiGroup: "design",
      }),
      field("navigation.mobile.hamburgerSize", SEGMENT_FIELD_TYPES.NUMBER, {
        label: "Vista móvil · tamaño hamburguesa (px)",
        limits: { min: 32, max: 72, step: 1 },
        uiGroup: "design",
      }),
      field("navigation.mobile.logoMaxHeight", SEGMENT_FIELD_TYPES.NUMBER, {
        label: "Vista móvil · alto máximo del logo (px)",
        limits: { min: 20, max: 96, step: 1 },
        uiGroup: "design",
      }),
      field("navigation.homeLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Inicio",
        maxLength: 80,
      }),
      field("navigation.aboutLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Sobre nosotros",
        maxLength: 100,
      }),
      field("navigation.highlightsLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Por qué elegirnos",
        maxLength: 100,
      }),
      field("navigation.productsLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Productos o servicios",
        maxLength: 80,
      }),
      field("navigation.allProductsLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Botón del mega menú",
        maxLength: 100,
      }),
      field("navigation.megaMenuStyle", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Estilo del mega menú",
        options: MEGA_MENU_STYLE_OPTIONS,
        uiGroup: "behavior",
      }),
      field("navigation.megaMenuContent", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Contenido del mega menú",
        options: MEGA_MENU_CONTENT_OPTIONS,
        uiGroup: "behavior",
      }),
      field("navigation.megaMenuDisplay", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Qué mostrar",
        options: MEGA_MENU_DISPLAY_OPTIONS,
        uiGroup: "behavior",
      }),
      field("navigation.megaMenuHierarchy", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Orden de categorías",
        options: MEGA_MENU_HIERARCHY_OPTIONS,
        uiGroup: "behavior",
      }),
      field("navigation.showMegaMenuIntro", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar bloque destacado del mega menú",
      }),
      field("navigation.megaMenuEyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior del mega menú",
        maxLength: 120,
      }),
      field("navigation.megaMenuTitle", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título del mega menú",
        maxLength: 180,
      }),
      field("navigation.megaMenuDescription", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Descripción del mega menú",
        maxLength: 500,
      }),
      field("navigation.showMegaMenuStats", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar cantidades en el bloque destacado",
      }),
      field("navigation.megaMenuCategoriesSubtitle", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto debajo del título de categorías",
        maxLength: 220,
      }),
      field("navigation.megaMenuSortLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto auxiliar de categorías",
        maxLength: 120,
      }),
      field("navigation.showMegaMenuLatest", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar últimos agregados",
      }),
      field("navigation.megaMenuLatestLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título de últimos agregados",
        maxLength: 120,
      }),
      field("navigation.loadingCategoriesText", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto mientras carga el mega menú",
        maxLength: 180,
      }),
      field("navigation.emptyCategoriesText", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto si no hay categorías",
        maxLength: 220,
      }),
      field("navigation.categoriesErrorText", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto si falla el mega menú",
        maxLength: 220,
      }),
      field("navigation.contactLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Contacto",
        maxLength: 80,
      }),
      field("navigation.loginLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Acceso de clientes",
        maxLength: 100,
      }),
      field("navigation.accountLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Descripción accesible de la cuenta",
        maxLength: 100,
      }),
      field("navigation.buttons", SEGMENT_FIELD_TYPES.COLLECTION, {
        label: "Botones de la navbar",
        navbarButtons: true,
        limits: { maxItems: 8 },
        item: {
          fields: [
            { key: "id", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 100 },
            { key: "label", label: "Texto del botón", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 80 },
            {
              key: "behavior",
              label: "Comportamiento",
              type: SEGMENT_FIELD_TYPES.SELECT,
              defaultValue: "section",
              options: [
                { value: "section", label: "Deslizar a una sección" },
                { value: "link", label: "Abrir un link" },
                { value: "mega-menu", label: "Desplegar mega menú" },
              ],
            },
            { key: "target", label: "Destino", type: SEGMENT_FIELD_TYPES.URL, maxLength: URL_MAX_LENGTH },
            {
              key: "megaMenuContent",
              label: "Contenido del mega menú",
              type: SEGMENT_FIELD_TYPES.SELECT,
              defaultValue: "inherit",
              options: [
                { value: "inherit", label: "Usar configuración general" },
                ...MEGA_MENU_CONTENT_OPTIONS,
              ],
            },
            {
              key: "megaMenuDisplay",
              label: "Qué mostrar",
              type: SEGMENT_FIELD_TYPES.SELECT,
              defaultValue: "inherit",
              options: [
                { value: "inherit", label: "Usar configuración general" },
                ...MEGA_MENU_DISPLAY_OPTIONS,
              ],
            },
            {
              key: "megaMenuHierarchy",
              label: "Jerarquía",
              type: SEGMENT_FIELD_TYPES.SELECT,
              defaultValue: "inherit",
              options: [
                { value: "inherit", label: "Usar configuración general" },
                ...MEGA_MENU_HIERARCHY_OPTIONS,
              ],
            },
          ],
        },
      }),
      field("navigation.homeUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino de Inicio",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("navigation.aboutUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino de Sobre nosotros",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("navigation.highlightsUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino de Por qué elegirnos",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("navigation.productsUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino de Productos o servicios",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("navigation.contactUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino de Contacto",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("navigation.loginUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino del acceso de clientes",
        maxLength: URL_MAX_LENGTH,
      }),
      field("navigation.showAboutLink", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar enlace Sobre nosotros",
      }),
      field("navigation.showHighlightsLink", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar enlace Por qué elegirnos",
      }),
      field("navigation.showProductsLink", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar enlace Productos",
      }),
      field("navigation.showContactLink", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar enlace Contacto",
      }),
      field("navigation.showCart", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar carrito",
      }),
      field("navigation.showCustomerAccess", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar acceso de clientes",
      }),
      field("navigation.showSocialLinks", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar redes sociales",
      }),
      field("navigation.menuAriaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Descripción accesible para abrir el menú",
        maxLength: 140,
      }),
      field("navigation.closeMenuAriaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Descripción accesible para cerrar el menú",
        maxLength: 140,
      }),
      field("navigation.cartAriaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Descripción accesible del carrito",
        maxLength: 140,
      }),
      field("business.name", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Nombre del negocio",
        maxLength: 140,
        shared: true,
      }),
      field("theme.logoUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Logo de la barra",
        maxLength: URL_MAX_LENGTH,
      }),
      field("theme.compactLogoUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Logo de la barra en celular",
        maxLength: URL_MAX_LENGTH,
      }),
      ...globalColorProperties(),
      visualStyleProperty(SEGMENT_TYPES.NAVBAR),
    ],
  }),
  [SEGMENT_TYPES.HERO]: commonSegment({
    id: SEGMENT_TYPES.HERO,
    visibilityPath: "sections.showHero",
    elementEditingReady: true,
    elementTargets: HERO_ELEMENT_TARGETS,
    shellPropertyPaths: HERO_SHELL_PROPERTY_PATHS,
    properties: [
      field("sections.showHero", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar portada",
      }),
      field("hero.eyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior",
        maxLength: 180,
      }),
      field("hero.title", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Título principal",
        maxLength: 220,
      }),
      field("hero.subtitle", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Texto de apoyo",
        maxLength: 900,
      }),
      field("hero.primaryCtaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Botón principal",
        maxLength: 100,
      }),
      field("hero.primaryCtaUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino del botón principal",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("hero.primaryCtaRequiresProducts", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "El botón principal requiere catálogo",
      }),
      field("hero.primaryCtaShowIcon", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar ícono en el botón principal",
        uiGroup: "behavior",
      }),
      field("hero.primaryCtaIcon", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Ícono del botón principal",
        options: HERO_CTA_ICON_OPTIONS,
        uiGroup: "behavior",
      }),
      ...heroCtaStyleProperties("hero.primaryCtaStyle", "Botón principal"),
      field("hero.secondaryCtaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Botón secundario",
        maxLength: 100,
      }),
      field("hero.secondaryCtaUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino del botón secundario",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("hero.secondaryCtaRequiresProducts", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "El botón secundario requiere catálogo",
      }),
      field("hero.secondaryCtaShowIcon", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar ícono en el botón secundario",
        uiGroup: "behavior",
      }),
      field("hero.secondaryCtaIcon", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Ícono del botón secundario",
        options: HERO_CTA_ICON_OPTIONS,
        uiGroup: "behavior",
      }),
      ...heroCtaStyleProperties("hero.secondaryCtaStyle", "Botón secundario"),
      field("hero.imageUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Imagen principal",
        maxLength: URL_MAX_LENGTH,
      }),
      field("hero.imagePosition", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición de la imagen",
        options: IMAGE_POSITION_OPTIONS,
      }),
      field("hero.contentHorizontalAlign", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición horizontal de los textos",
        options: [
          { value: "left", label: "Izquierda" },
          { value: "center", label: "Centro" },
          { value: "right", label: "Derecha" },
        ],
        uiGroup: "behavior",
      }),
      field("hero.contentVerticalAlign", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición vertical de los textos",
        options: [
          { value: "top", label: "Arriba" },
          { value: "center", label: "Centro" },
          { value: "bottom", label: "Abajo" },
        ],
        uiGroup: "behavior",
      }),
      field("hero.overlay", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Capa sobre la imagen",
        maxLength: 600,
      }),
      field("hero.rotatingPhrases", SEGMENT_FIELD_TYPES.COLLECTION, {
        label: "Frases rotativas",
        limits: { maxItems: 12 },
        item: {
          type: SEGMENT_FIELD_TYPES.TEXT,
          maxLength: 180,
        },
      }),
      field("hero.slides", SEGMENT_FIELD_TYPES.COLLECTION, {
        label: "Imágenes de portada",
        limits: { maxItems: 6 },
        item: {
          fields: [
            { key: "url", label: "Imagen", type: SEGMENT_FIELD_TYPES.IMAGE, maxLength: URL_MAX_LENGTH },
            { key: "alt", label: "Texto alternativo", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 220 },
            { key: "title", label: "Título", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 220 },
            { key: "subtitle", label: "Subtítulo", type: SEGMENT_FIELD_TYPES.TEXTAREA, maxLength: 700 },
            { key: "position", label: "Posición de la imagen", type: SEGMENT_FIELD_TYPES.SELECT, options: IMAGE_POSITION_OPTIONS },
          ],
        },
      }),
      ...globalColorProperties(),
      visualStyleProperty(SEGMENT_TYPES.HERO),
    ],
  }),
};

export default structureSegments;
